import type { AgentBusinessInfo } from '@/types/Agents';
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  parsePhoneNumber,
} from '@/utils/phoneUtils';

export interface BusinessInfoFormValue {
  businessName: string;
  website: string;
  address: string;
  serviceArea: string;
  publicEmail: string;
  publicPhoneCountryCode: string;
  publicPhoneLocalNumber: string;
  extraNotes: string;
}

export const EMPTY_BUSINESS_INFO_FORM: BusinessInfoFormValue = {
  businessName: '',
  website: '',
  address: '',
  serviceArea: '',
  publicEmail: '',
  publicPhoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
  publicPhoneLocalNumber: '',
  extraNotes: '',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const trim = (value: string | undefined): string => (value ?? '').trim();

export const isValidBusinessEmail = (value: string): boolean => {
  const normalized = trim(value);
  return normalized.length === 0 || EMAIL_PATTERN.test(normalized);
};

export const isValidBusinessWebsite = (value: string): boolean => {
  const normalized = trim(value);
  if (!normalized) return true;
  try {
    const withProtocol = /^https?:\/\//i.test(normalized)
      ? normalized
      : `https://${normalized}`;
    const url = new URL(withProtocol);
    return url.hostname.includes('.');
  } catch {
    return false;
  }
};

export const normalizeWebsite = (value: string): string => {
  const normalized = trim(value);
  if (!normalized) return '';
  if (/^https?:\/\//i.test(normalized)) return normalized;
  return `https://${normalized}`;
};

export const compactBusinessInfo = (
  info: AgentBusinessInfo
): AgentBusinessInfo => {
  const compacted: AgentBusinessInfo = {};
  (Object.keys(info) as (keyof AgentBusinessInfo)[]).forEach(key => {
    const value = trim(info[key]);
    if (value) {
      compacted[key] = value;
    }
  });
  return compacted;
};

export const formValueToBusinessInfo = (
  form: BusinessInfoFormValue,
  extras?: { businessName?: string; about?: string }
): AgentBusinessInfo => {
  const publicPhone = form.publicPhoneLocalNumber.trim()
    ? `${form.publicPhoneCountryCode}${form.publicPhoneLocalNumber.trim()}`
    : '';

  return compactBusinessInfo({
    business_name: extras?.businessName ?? form.businessName,
    about: extras?.about,
    website: normalizeWebsite(form.website),
    address: form.address,
    service_area: form.serviceArea,
    public_phone: publicPhone,
    public_email: trim(form.publicEmail).toLowerCase(),
    extra_notes: form.extraNotes,
  });
};

export const businessInfoToFormValue = (
  info?: AgentBusinessInfo | null,
  fallbackBusinessName = ''
): BusinessInfoFormValue => {
  const parsedPhone = parsePhoneNumber(info?.public_phone);
  return {
    businessName: info?.business_name ?? fallbackBusinessName,
    website: info?.website ?? '',
    address: info?.address ?? '',
    serviceArea: info?.service_area ?? '',
    publicEmail: info?.public_email ?? '',
    publicPhoneCountryCode: parsedPhone.code,
    publicPhoneLocalNumber: parsedPhone.local,
    extraNotes: info?.extra_notes ?? '',
  };
};

export const formatBusinessInfoForPrompt = (
  info: AgentBusinessInfo
): string => {
  const lines: string[] = [];
  if (trim(info.business_name)) {
    lines.push(`Business name: ${trim(info.business_name)}`);
  }
  if (trim(info.website)) lines.push(`Website: ${trim(info.website)}`);
  if (trim(info.address)) lines.push(`Address: ${trim(info.address)}`);
  if (trim(info.service_area)) {
    lines.push(`Service area: ${trim(info.service_area)}`);
  }
  if (trim(info.public_phone)) {
    lines.push(`Public phone: ${trim(info.public_phone)}`);
  }
  if (trim(info.public_email)) {
    lines.push(`Public email: ${trim(info.public_email)}`);
  }
  if (trim(info.extra_notes)) {
    lines.push(`Additional notes: ${trim(info.extra_notes)}`);
  }
  return lines.join('\n');
};
